/**
 * Theme module (SPEC §6): resolves the starting theme (stored choice, else OS
 * preference), applies it to chrome and viewport before the editor appears,
 * and owns `theme.set`.
 *
 * The visitor's choice is System (the default: follow the OS color scheme
 * live) or a named theme, which persists and ignores the OS. Choosing System
 * again goes back to following it.
 */
import { CommandsModule } from "../../commands/CommandsModule";
import { parsePayload } from "../../commands/payload";
import type { CommandDefinition } from "../../commands/types";
import type { Unsubscribe } from "../../core/Emitter";
import { CoreModule } from "../../core/CoreModule";
import { defineModule } from "../../core/module";
import { VersionedStore } from "../../core/store";
import { RendererModule } from "../../render/RendererModule";
import { defaultThemeFor, findTheme } from "../../themes";
import type { Theme } from "../../themes";
import { SYSTEM_THEME } from "./themeBoot";
import { resolveInitialTheme, ThemeService } from "./ThemeService";
import type { ThemeTarget } from "./ThemeService";

/** Host adapters: DOM and localStorage in the app, fakes in tests. */
export interface ThemeEnvironment {
  readonly target: ThemeTarget;
  readonly readStoredId: () => string | null;
  readonly writeStoredId: (id: string) => void;
  readonly prefersLight: () => boolean;
  /** Calls back when `prefers-color-scheme` changes. */
  readonly watchPrefersLight: (listener: (light: boolean) => void) => Unsubscribe;
}

export const ThemeModule = defineModule({
  name: "Theme",
  dependsOn: [CoreModule, CommandsModule, RendererModule],
  options: (): ThemeEnvironment => {
    throw new Error("Theme needs host adapters: call engine.configure(ThemeModule, …)");
  },
  init: ({ deps: { Core, Commands, Renderer }, options }) => {
    const service = new ThemeService({
      target: options.target,
      backend: Renderer.backend,
      scheduler: Core.scheduler,
      requestFrame: (reason) => {
        Core.frameLoop.invalidate(reason);
      },
    });

    const storedId = safely(options.readStoredId, null);
    const named = storedId !== null && findTheme(storedId) !== undefined;
    const choice = new VersionedStore<string>(Core.scheduler, named ? storedId : SYSTEM_THEME);
    service.apply(resolveInitialTheme({ storedId, prefersLight: options.prefersLight() }));

    // Following the OS is environment state, like the initial resolution, not a
    // visitor edit, so it applies directly rather than through a command.
    const osDefault = () => defaultThemeFor(options.prefersLight() ? "light" : "dark");
    const stopFollowing = options.watchPrefersLight(() => {
      if (choice.getSnapshot() === SYSTEM_THEME) service.apply(osDefault());
    });

    Commands.bus.register(
      themeCommands(service, osDefault, (id) => {
        choice.publish(id);
        safely(() => {
          options.writeStoredId(id);
        }, undefined);
      }),
    );

    return { theme: service, choice: choice, stopFollowing };
  },
  shutdown: ({ stopFollowing }) => {
    stopFollowing();
  },
});

/**
 * `theme.set { id }`: a theme id, or `system` to follow the OS. Journaled, not
 * undoable (A-15), and persisted as the visitor's choice.
 */
function themeCommands(
  service: ThemeService,
  osDefault: () => Theme,
  onChosen: (id: string) => void,
): CommandDefinition[] {
  const set: CommandDefinition<{ id: string }, undefined> = {
    type: "theme.set",
    undoable: false,
    validate: (raw) => {
      const shape = parsePayload(raw, { id: "string" });
      if (!shape.ok) return shape;
      if (shape.value.id !== SYSTEM_THEME && !findTheme(shape.value.id))
        return { ok: false, error: `Unknown theme '${shape.value.id}'` };
      return { ok: true, value: { id: shape.value.id } };
    },
    do: ({ id }) => {
      const theme = id === SYSTEM_THEME ? osDefault() : findTheme(id);
      if (theme) service.apply(theme);
      onChosen(id);
      return undefined;
    },
    undo: () => undefined,
    describe: ({ id }) => `Theme: ${id === SYSTEM_THEME ? "System" : (findTheme(id)?.name ?? id)}`,
  };
  return [set] as CommandDefinition[];
}

/** Storage throws in some privacy modes; the theme then just doesn't persist. */
function safely<T>(action: () => T, fallback: T): T {
  try {
    return action();
  } catch {
    return fallback;
  }
}
